import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usersApi, CompanyProfile } from '../api/users';
import { ROLE_LABELS } from '../types/user';

const Profile: React.FC = () => {
  const { user, logout, isAdmin } = useAuth();
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [name, setName] = useState('');
  const [unp, setUnp] = useState('');
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [unpError, setUnpError] = useState('');

  useEffect(() => {
    const abortController = new AbortController();

    usersApi.getCompanyProfile(abortController.signal)
      .then((data) => {
        if (abortController.signal.aborted) return;
        setProfile(data);
        setName(data.name || '');
        setUnp(data.unp || '');
        setAddress(data.address || '');
      })
      .catch((err: any) => {
        if (abortController.signal.aborted) return;
        console.error('Profile load error:', {
          status: err.response?.status,
          data: err.response?.data,
          message: err.message,
          code: err.code,
        });
        const serverMessage = Array.isArray(err.response?.data?.message)
          ? err.response.data.message.join(', ')
          : err.response?.data?.message;
        setMessage({
          type: 'error',
          text: serverMessage || `Ошибка загрузки профиля (${err.message || 'нет подключения к серверу'})`,
        });
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      });

    return () => abortController.abort();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUnpError('');
    setMessage(null);

    if (!(unp || '').trim()) {
      setUnpError('УНП обязателен');
      return;
    }

    if ((unp || '').trim().length !== 9) {
      setUnpError('УНП должен содержать 9 цифр');
      return;
    }

    setSaving(true);
    try {
      const updated = await usersApi.updateCompanyProfile({
        name: (name || '').trim(),
        unp: (unp || '').trim(),
        address: (address || '').trim(),
      });
      setProfile(updated);
      setName(updated.name || '');
      setUnp(updated.unp || '');
      setAddress(updated.address || '');
      setMessage({ type: 'success', text: 'Профиль сохранён' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err: any) {
      console.error('Profile save error:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
        code: err.code,
      });
      const serverMessage = Array.isArray(err.response?.data?.message)
        ? err.response.data.message.join(', ')
        : err.response?.data?.message;
      setMessage({
        type: 'error',
        text: serverMessage || `Ошибка сохранения (${err.message || 'нет подключения к серверу'})`,
      });
      setTimeout(() => setMessage(null), 10000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white shadow">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between gap-2">
          <h1 className="text-lg sm:text-2xl font-bold text-gray-800">Профиль компании</h1>
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="text-blue-600 hover:underline text-sm whitespace-nowrap">
              Назад
            </Link>
            <button onClick={logout} className="text-gray-500 hover:text-gray-700 text-sm whitespace-nowrap">
              Выйти
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {message && (
          <div
            className={`mb-4 p-3 rounded-md text-sm ${
              message.type === 'success'
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* User Info Card */}
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Пользователь</h2>
            <div className="flex items-center gap-3 mb-4">
              {user?.avatar ? (
                <img src={user.avatar} alt={user.name} className="w-12 h-12 rounded-full" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-lg">
                  {user?.name?.charAt(0) || '?'}
                </div>
              )}
              <div>
                <div className="text-sm font-medium text-gray-800">{user?.name}</div>
                <div className="text-xs text-gray-500">{user?.email}</div>
              </div>
            </div>
            <div className="border-t border-gray-100 pt-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Роль</span>
                <span className="font-medium text-gray-800">
                  {user?.role ? ROLE_LABELS[user.role] : '—'}
                </span>
              </div>
            </div>
          </div>

          {/* Company Profile Form */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Реквизиты компании</h2>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : isAdmin ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    УНП <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={9}
                    value={unp}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '');
                      setUnp(digits);
                      if (unpError) setUnpError('');
                    }}
                    placeholder="123456789"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      unpError ? 'border-red-300' : 'border-gray-300'
                    }`}
                    disabled={saving}
                  />
                  {unpError && (
                    <p className="mt-1 text-sm text-red-600">{unpError}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Название фирмы
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="ООО «Ромашка»"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={saving}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Адрес фирмы
                  </label>
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="г. Минск, ул. Примерная, д. 1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={saving}
                  />
                </div>

                <div className="pt-3 flex justify-end">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                  >
                    {saving && (
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    )}
                    {saving ? 'Сохранение...' : 'Сохранить'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-1">УНП</span>
                  <p className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-800">{profile?.unp || '—'}</p>
                </div>
                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-1">Название фирмы</span>
                  <p className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-800">{profile?.name || '—'}</p>
                </div>
                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-1">Адрес фирмы</span>
                  <p className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-800">{profile?.address || '—'}</p>
                </div>
                <p className="text-sm text-gray-500 italic">Редактировать профиль компании может только руководитель.</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Profile;
